import { useMemo } from 'react'
import { Avatar } from '@/components/Avatar'
import { FormGroup } from '@/components/Form'
import {
  CustomSelect,
  SingleItemSelectedDefault,
} from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { SearchResultItem } from '@/components/inputs/SearchResultItem'
import { observer } from 'mobx-react-lite'
import { type WorkspaceSelectorViewModel } from '@/features/jobs/drafting/WorkspaceSelectorViewModel'

export interface CompactWorkspaceSelectorContainerProps {
  vm: WorkspaceSelectorViewModel
}

export const CompactWorkspaceSelectorContainer = observer(
  function CompactWorkspaceSelectorContainer({
    vm,
  }: CompactWorkspaceSelectorContainerProps) {
    const itemSource = useMemo(
      () =>
        ItemSource.static(vm.availableWorkspaces, (item, text) =>
          item.name.toLowerCase().includes(text.toLowerCase()),
        ),
      [vm.availableWorkspaces],
    )
    const selectProps = useCustomSelectStateFromItemSource({
      itemSource,
    })
    return (
      <FormGroup>
        <CustomSelect
          {...selectProps}
          placeholder="Select a Workspace"
          itemToString={(v) => String(v?.name)}
          isDisabled={vm.disabled}
          focus$={vm.step.becomeActive$}
          onFocus={vm.step.onBecameActive}
          value={vm.selectedWorkspace ?? undefined}
          onChange={vm.selectWorkspace}
          multi={false}
          testId="compact-workspace-selector"
          renderItem={(itemProps) => (
            <SingleItemSelectedDefault
              {...itemProps}
              visual={
                <Avatar
                  size={24}
                  subject={{
                    displayName: itemProps.item.name,
                    avatar: itemProps.item.branding.logos.icon,
                  }}
                />
              }
            />
          )}
          renderSearchItem={({
            item,
            itemToString: _itemToString,
            ...itemProps
          }) => (
            <SearchResultItem
              {...itemProps}
              visual={
                <Avatar
                  size={24}
                  subject={{
                    avatar: item.branding.logos.icon,
                    displayName: item.name,
                  }}
                />
              }
            >
              {item.name}
            </SearchResultItem>
          )}
        />
      </FormGroup>
    )
  },
)
